"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.letters = exports.isNotChapter = exports.isChapter = exports.removeWhitespace = exports.splitBookChapters = exports.firstVerseNumber = exports.chapterVerseIterator = exports.isNewTestament = exports.getFirstChapterVerse = exports.getBookName = exports.getVerseRange = exports.getTitle = exports.getParseableReference = exports.getReference = void 0;
const newTestamentShortcodes_json_1 = __importDefault(require("../data/newTestamentShortcodes.json"));
/**
 * Gets the reference of a video.
 *
 * @param meta Video metadata of BBBVideo type
 * @returns The reference of a video. Example: "Genesis 1:1-31; 2:1-4"
 */
const getReference = (meta) => { var _a, _b; return (_b = (_a = meta.title_native) !== null && _a !== void 0 ? _a : meta.title) !== null && _b !== void 0 ? _b : ""; };
exports.getReference = getReference;
/**
 * Gets the parseable reference of a video.
 *
 * @param meta Video metadata of BBBVideo type
 * @returns The parseable reference of a video. Example: "Genesis 1:1-31; 2:1-4"
 */
const getParseableReference = (meta) => { var _a; return (_a = meta.parseable_title) !== null && _a !== void 0 ? _a : (0, exports.getReference)(meta); };
exports.getParseableReference = getParseableReference;
/**
 * Gets the title of a video.
 *
 * @param meta Video metadata of BBBVideo type
 * @returns The title of a video. Example: "God creates the heavens and the earth"
 */
const getTitle = (meta) => { var _a, _b; return (_b = (_a = meta.subtitle_native) !== null && _a !== void 0 ? _a : meta.subtitle) !== null && _b !== void 0 ? _b : ""; };
exports.getTitle = getTitle;
/**
 * Appends ":1" to the end of a chapter/verse string if the verse is missing.
 *
 * @param chapterVerse A string of the chapter and verse. Example: "1:1". The verse is optional. Example: "1"
 * @returns If a verse range is present, return the same string. If a verse range is not present, return the same string with ":1" appended to the end.
 */
const fixMissingVerse = (chapterVerse) => {
    if (!chapterVerse.includes(":")) {
        return `${chapterVerse}:1`;
    }
    return chapterVerse;
};
/**
 * Gets the verse range of a chapter in string format.
 *
 * @param meta The video metadata of BBBVideo type
 * @param chapter The chapter number in string format. Example: "1"
 * @returns The verse range of a chapter in string format. Example: "1-3"
 */
const getVerseRange = (meta, chapterNumber) => {
    const ref = (0, exports.getParseableReference)(meta);
    const [_, chapters] = (0, exports.splitBookChapters)(ref);
    for (const chapter of chapters) {
        const [currentChapterNumber, verseRange] = chapter.split(":");
        if (currentChapterNumber === chapterNumber) {
            return verseRange;
        }
    }
    return "";
};
exports.getVerseRange = getVerseRange;
/**
 * Gets the book name of a video.
 *
 * @param meta Video metadata of BBBVideo type
 * @returns The book name of a video. Example: "Genesis"
 */
const getBookName = (meta) => {
    const ref = (0, exports.getParseableReference)(meta);
    const [book, _] = (0, exports.splitBookChapters)(ref);
    return book;
};
exports.getBookName = getBookName;
/**
 * Gets the first chapter and verse of a video.
 *
 * @param video Video metadata of BBBVideo type
 * @returns The first chapter and verse of a video. Example: ["1", "1"]
 */
const getFirstChapterVerse = (video) => {
    const iterator = chapterVerseIterator(video);
    const first = iterator.next();
    if (!first.done) {
        const [chapter, verse] = first.value;
        return [chapter, verse];
    }
    return ["", ""];
};
exports.getFirstChapterVerse = getFirstChapterVerse;
/**
 * Checks if a book is in the New Testament.
 *
 * @param bookShortcode A bible book in shortcode format. Case insensitive. Examples: "GEN" or "mat"
 * @returns true if the book is in the New Testament, false otherwise
 */
const isNewTestament = (bookShortcode) => newTestamentShortcodes_json_1.default.some((book) => book.toLowerCase() === bookShortcode.toLowerCase());
exports.isNewTestament = isNewTestament;
/**
 *
 * Creates an iterator that iterates through all chapters and verses of a video.
 * The iterator returns an array of two strings: [chapter, verse]. Example: ["1", "1"], ["1", "2"], ["1", "3"], ...
 *
 * @param meta Video metadata of BBBVideo type
 * @returns An iterator that iterates through all chapters and verses of a video
 */
function* chapterVerseIterator(meta) {
    const ref = (0, exports.getParseableReference)(meta);
    const [_, chapters] = (0, exports.splitBookChapters)(ref);
    for (const chapter of chapters) {
        const fixedChapter = fixMissingVerse(chapter);
        const [num, verseRange] = fixedChapter.split(":");
        const verses = arrayizeVerseRange(verseRange);
        for (const verse of verses) {
            yield [num, verse];
        }
    }
}
exports.chapterVerseIterator = chapterVerseIterator;
function firstVerseNumber(meta, chapter) {
    const ref = (0, exports.getParseableReference)(meta);
    const [_, chapters] = (0, exports.splitBookChapters)(ref);
    for (const ch of chapters) {
        const [num, verseRange] = ch.split(":");
        return verseRange.split(/;|,/)[0].split("-")[0];
    }
    return undefined;
}
exports.firstVerseNumber = firstVerseNumber;
/**
 * Converts a verse range string into an array of verse numbers.
 *
 * @param verseRange A string of the verse range. Examples: "1-3", "1,3,5", "1,3-5
 * @returns An array of all numbers in the verse range. Example: "1-3" => ["1", "2", "3"]
 */
const arrayizeVerseRange = (verseRange) => {
    const sections = verseRange.split(/,|;/);
    return sections.reduce((acc, cur) => {
        const verseEnds = cur.split("-");
        if (verseEnds.length === 1) {
            acc.push(verseEnds[0]);
        }
        else {
            for (let i = parseInt(verseEnds[0], 10); i <= parseInt(verseEnds[1], 10); i += 1) {
                acc.push(i.toString());
            }
        }
        return acc;
    }, []);
};
const splitBookChapters = (ref) => {
    const fixedRef = fixMissingVerse(ref);
    const bookChapter = fixedRef.split(":")[0];
    const index = bookChapter.lastIndexOf(" ");
    const book = bookChapter.slice(0, index);
    const sections = (0, exports.removeWhitespace)(fixedRef.slice(index + 1))
        .toLowerCase()
        .split(/;|,/);
    const chapters = sections.reduce((acc, cur) => {
        if ((0, exports.isChapter)(cur)) {
            acc.push(cur);
        }
        else {
            const last = acc[acc.length - 1];
            acc[acc.length - 1] = `${last},${cur}`;
        }
        return acc;
    }, []);
    return [book, chapters];
};
exports.splitBookChapters = splitBookChapters;
const removeWhitespace = (input) => input.replace(/\s/g, "");
exports.removeWhitespace = removeWhitespace;
const isChapter = (ref) => {
    return /^\d+:\d/.test(ref);
};
exports.isChapter = isChapter;
const isNotChapter = (ref) => !(0, exports.isChapter)(ref);
exports.isNotChapter = isNotChapter;
const letters = (char) => {
    const length = char.toLowerCase().charCodeAt(0) - 96;
    return Array.from({ length }, (_, i) => {
        return String.fromCharCode(97 + i);
    });
};
exports.letters = letters;
